import React, { useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './config/amplify';

import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { BottomNav } from './components/ui/BottomNav';
import { InstallBanner } from './components/ui/InstallBanner';
import { NotificationBell } from './components/ui/NotificationBell';
import { ListSelector } from './components/ui/ListSelector';
import { usePremiumNotifications } from './hooks/usePremiumNotifications';
import { useOfflineSync } from './hooks/useOfflineSync';

import { LoginScreen } from './screens/LoginScreen';
import { DataDeletionStatusScreen } from './screens/DataDeletionStatusScreen';
import { AppShell } from './components/ui/AppShell';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { AcceptInviteScreen } from './screens/AcceptInviteScreen';
import { ItemsScreen } from './screens/ItemsScreen';
import { ItemFormScreen } from './screens/ItemFormScreen';
import { PriceHistoryScreen } from './screens/PriceHistoryScreen';
import { ShoppingHomeScreen } from './screens/ShoppingHomeScreen';
import { ActiveSessionScreen } from './screens/ActiveSessionScreen';
import { ChatScreen } from './screens/ChatScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { SessionDetailScreen } from './screens/SessionDetailScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { PaymentScreen } from './screens/PaymentScreen';
import { AboutScreen } from './screens/AboutScreen';
import { AdminScreen } from './screens/AdminScreen';
import { CollaboratorsScreen } from './screens/CollaboratorsScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 300_000,
      retry: 1,
    },
  },
});

function UpdateBanner({ onUpdate }: { onUpdate: () => void }) {
  return (
    <div className="fixed top-0 left-0 right-0 z-[200] pointer-events-none safe-area-inset-top">
      <div className="mx-auto w-full max-w-md bg-emerald-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg pointer-events-auto">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="shrink-0">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <p className="text-sm font-medium truncate">Nova versão disponível</p>
        </div>
        <button
          onClick={onUpdate}
          className="text-sm font-bold bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg touch-manipulation shrink-0"
        >
          Atualizar
        </button>
      </div>
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthContext();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
          <p className="text-sm text-gray-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function MainLayout() {
  usePremiumNotifications();
  useOfflineSync();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <AppShell>
      <div className="flex flex-col min-h-screen">
        <InstallBanner />
        <ListSelector open={menuOpen} onClose={() => setMenuOpen(false)} />
        <Outlet />
        <NotificationBell />
        <BottomNav onOpenMenu={() => setMenuOpen(true)} />
      </div>
    </AppShell>
  );
}

function AppRoutes() {
  const [showUpdate, setShowUpdate] = useState(false);
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const location = useLocation();

  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    import('./serviceWorkerRegistration').then(({ register }) => {
      register({
        onUpdate: (registration) => {
          swRegistrationRef.current = registration;
          setShowUpdate(true);
        },
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualUpdate = () => {
    if (swRegistrationRef.current) {
      import('./serviceWorkerRegistration').then(({ applyUpdate }) => {
        applyUpdate(swRegistrationRef.current!);
      });
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      {showUpdate && <UpdateBanner onUpdate={handleManualUpdate} />}
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <AppShell>
              <LoginScreen />
            </AppShell>
          }
        />
        <Route path="/accept-invite" element={<AcceptInviteScreen />} />
        <Route path="/data-deletion" element={<DataDeletionStatusScreen />} />

        {/* Onboarding — accessible when user has no household */}
        <Route
          path="/onboarding"
          element={
            <AuthGuard>
              <OnboardingScreen />
            </AuthGuard>
          }
        />

        <Route
          path="/invite/:token"
          element={
            <AuthGuard>
              <AcceptInviteScreen />
            </AuthGuard>
          }
        />

        {/* Main layout routes (with bottom nav) — no household required */}
        <Route
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route path="/" element={<Navigate to="/items" replace />} />
          <Route path="/items" element={<ItemsScreen />} />
          <Route path="/items/new" element={<ItemFormScreen />} />
          <Route path="/items/:id/edit" element={<ItemFormScreen />} />
          <Route path="/items/:id/price-history" element={<PriceHistoryScreen />} />
          <Route path="/shopping" element={<ShoppingHomeScreen />} />
          <Route path="/shopping/active" element={<ActiveSessionScreen />} />
          <Route path="/chat" element={<ChatScreen />} />
          <Route path="/history" element={<HistoryScreen />} />
          <Route path="/history/:id" element={<SessionDetailScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/payment" element={<PaymentScreen />} />
          <Route path="/about" element={<AboutScreen />} />
          <Route path="/admin" element={<AdminScreen />} />
          <Route path="/collaborators/:householdId" element={<CollaboratorsScreen />} />
          <Route path="/notifications" element={<NotificationsScreen />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
